import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { api, isApiError } from '@/lib/api';
import styles from './parity.module.css';
export interface BookField {
  key: string;
  label: string;
  value: string;
  type?: 'text' | 'date' | 'number';
  options?: { value: string; label: string }[];
  maxLength?: number;
  min?: number;
  max?: number;
  required?: boolean;
}
export function BooksForm({
  title,
  path,
  fields,
  build,
  onClose,
}: {
  title: string;
  path: string;
  fields: BookField[];
  build: (values: Record<string, string>) => Record<string, unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.value])),
  );
  const [error, setError] = useState<unknown>();
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(path, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['books'] });
      onClose();
      toast.show({ tone: 'success', title: t('common.saved') });
    },
  });
  return (
    <Dialog
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            form="books-form"
            variant="primary"
            loading={save.isPending}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        id="books-form"
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          try {
            setError(undefined);
            void save.mutateAsync(build(values)).catch(setError);
          } catch (err) {
            setError(err);
          }
        }}
      >
        {fields.map((f) => (
          <Field key={f.key} label={f.label}>
            {f.options ? (
              <Select
                value={values[f.key]}
                onChange={(e) =>
                  setValues({ ...values, [f.key]: e.target.value })
                }
                options={f.options}
              />
            ) : (
              <Input
                type={f.type ?? 'text'}
                value={values[f.key]}
                required={f.required ?? true}
                maxLength={f.maxLength}
                min={f.min}
                max={f.max}
                onChange={(e) =>
                  setValues({ ...values, [f.key]: e.target.value })
                }
              />
            )}
          </Field>
        ))}
        {error !== undefined && (
          <ErrorState
            error={error}
            title={
              error instanceof Error && !isApiError(error)
                ? error.message
                : undefined
            }
          />
        )}
      </form>
    </Dialog>
  );
}
